import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createVNode as _createVNode, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, createBlock as _createBlock, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = {
  class: "scene-graph",
  "aria-label": "场景示意图"
}
const _hoisted_2 = { class: "graph-toolbar" }
const _hoisted_3 = { class: "graph-actions" }
const _hoisted_4 = ["title"]
const _hoisted_5 = { class: "area-kind" }
const _hoisted_6 = { key: 0 }
const _hoisted_7 = {
  key: 1,
  class: "area-count"
}
const _hoisted_8 = {
  key: 2,
  class: "area-empty"
}
const _hoisted_9 = {
  key: 1,
  class: "graph-empty"
}
const _hoisted_10 = { class: "graph-footer" }
const _hoisted_11 = { key: 1 }

import { computed, inject, nextTick, onActivated, onBeforeUnmount, onDeactivated, ref, watch } from "vue";
import { studioEditorActiveKey } from "../studioSessionHistory";
import { VueFlow, Handle, Position, MarkerType, useVueFlow, type Node, type Edge, type NodeMouseEvent, type NodeDragEvent } from "@vue-flow/core";
import { Background } from "@vue-flow/background";
import type { SceneProject } from "./sceneProject";
import { buildSceneGraph, type SceneSelection } from "./sceneGraph";
import "@vue-flow/core/dist/style.css";
import "@vue-flow/core/dist/theme-default.css";


export default /*@__PURE__*/_defineComponent({
  __name: 'SceneGraph',
  props: {
    project: {},
    selection: {}
  },
  emits: ["select", "edit"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;
const flowId = `scene-graph-${crypto.randomUUID()}`;
const editorActive = inject(studioEditorActiveKey, () => true);
const { fitView, zoomIn, zoomOut, panBy } = useVueFlow(flowId);
const nodes = ref<Node[]>([]);
const edges = ref<Edge[]>([]);
const positions = new Map<string, { x: number; y: number }>();
const projection = computed(() => buildSceneGraph(props.project));
const selectedBox = computed(() => projection.value.boxes.find(box => box.data.selection?.kind === props.selection.kind && box.data.selection.index === props.selection.index));
const panning = ref(false);
let disposed = false;
let active = true;
let cancelPan: (() => void) | undefined;
function fit() { void nextTick(() => { if (!disposed && active && editorActive()) void fitView({ padding: .18, duration: 250, maxZoom: 1 }); }); }
onActivated(() => { active = true; fit(); });
onDeactivated(() => { active = false; cancelPan?.(); });
watch(editorActive, active => { if (!active) cancelPan?.(); }, { flush: "sync" });
function sync() {
  nodes.value = projection.value.boxes.map(box => ({ id: box.id, type: "scene-area", position: positions.get(box.id) ?? box.position,
    parentNode: box.parentId, width: box.width, height: box.height, data: box.data,
    draggable: !box.parentId, dragHandle: ".area-heading", connectable: false, selectable: false,
    zIndex: box.data.kind === "world" ? 0 : box.data.kind === "main" ? 1 : 2 }));
  edges.value = projection.value.edges.map(edge => ({ ...edge, sourceHandle: "out", targetHandle: "in", type: "smoothstep",
    style: { stroke: "#185abd", strokeWidth: 3 }, zIndex: 5, selectable: false,
    markerEnd: { type: MarkerType.ArrowClosed, color: "#185abd", width: 22, height: 22 },
    labelStyle: { fill: "#17417b", fontSize: 11 }, labelBgStyle: { fill: "#fff", fillOpacity: .95 }, labelBgPadding: [6, 4] }));
}
watch(() => props.project, () => { positions.clear(); cancelPan?.(); }, { flush: "sync" });
watch(projection, sync, { immediate: true });
function arrange() { positions.clear(); sync(); fit(); }
function selectNode({ node }: NodeMouseEvent) { if (node.data.selection) emit("select", node.data.selection); }
function editNode({ node }: NodeMouseEvent) { if (node.data.selection) emit("edit", node.data.selection); }
function remember({ node }: NodeDragEvent) { if (!node.parentNode) positions.set(node.id, { ...node.position }); }
function isSelected(data: { selection?: SceneSelection }) { return data.selection?.kind === props.selection.kind && data.selection.index === props.selection.index; }
function startPan(event: PointerEvent) {
  if (!editorActive()) return;
  const target = event.target as Element | null;
  const background = target?.closest(".area-group") && !target.closest(".area-heading");
  if (event.button !== 2 && !(event.button === 0 && background)) return;
  event.preventDefault(); event.stopPropagation(); cancelPan?.();
  const pointerId = event.pointerId;
  let x = event.clientX, y = event.clientY;
  panning.value = true;
  const move = (next: PointerEvent) => {
    if (next.pointerId !== pointerId) return;
    next.preventDefault(); panBy({ x: next.clientX - x, y: next.clientY - y });
    x = next.clientX; y = next.clientY;
  };
  const end = (next: PointerEvent) => { if (next.pointerId === pointerId) cleanup(); };
  const cleanup = () => {
    window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", end);
    window.removeEventListener("pointercancel", end); window.removeEventListener("blur", cleanup);
    panning.value = false; cancelPan = undefined;
  };
  cancelPan = cleanup;
  window.addEventListener("pointermove", move, { passive: false }); window.addEventListener("pointerup", end);
  window.addEventListener("pointercancel", end); window.addEventListener("blur", cleanup);
}
onBeforeUnmount(() => { disposed = true; cancelPan?.(); });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[6] || (_cache[6] = _createElementVNode("span", null, "世界 → 一级区域 → 二级区域", -1)),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("button", {
          type: "button",
          onClick: arrange
        }, "整理布局"),
        _createElementVNode("button", {
          type: "button",
          onClick: fit
        }, "适应画布"),
        _createElementVNode("button", {
          type: "button",
          "aria-label": "缩小场景示意图",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(zoomOut)()))
        }, "−"),
        _createElementVNode("button", {
          type: "button",
          "aria-label": "放大场景示意图",
          onClick: _cache[1] || (_cache[1] = ($event: any) => (_unref(zoomIn)()))
        }, "＋")
      ])
    ]),
    _cache[7] || (_cache[7] = _createElementVNode("p", { class: "graph-guide" }, [
      _createElementVNode("span", { class: "link-legend" }, "世界连接"),
      _createTextVNode("拖动世界标题整体移动 · 空白区域或右键拖动画布 · 双击区域编辑属性")
    ], -1)),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(projection.value.warnings, (warning) => {
      return (_openBlock(), _createElementBlock("p", {
        key: warning,
        class: "graph-warning",
        role: "status"
      }, _toDisplayString(warning), 1))
    }), 128)),
    _createElementVNode("div", {
      class: _normalizeClass(["graph-canvas", { 'is-panning': panning.value }]),
      onPointerdownCapture: startPan,
      onContextmenu: _cache[4] || (_cache[4] = _withModifiers(() => {}, ["prevent"]))
    }, [
      (nodes.value.length)
        ? (_openBlock(), _createBlock(_unref(VueFlow), {
            key: 0,
            id: flowId,
            nodes: nodes.value,
            "onUpdate:nodes": _cache[2] || (_cache[2] = ($event: any) => ((nodes).value = $event)),
            edges: edges.value,
            "onUpdate:edges": _cache[3] || (_cache[3] = ($event: any) => ((edges).value = $event)),
            "min-zoom": .08,
            "max-zoom": 1.6,
            "pan-on-drag": [0],
            "delete-key-code": null,
            "disable-keyboard-a11y": !_unref(editorActive)(),
            "nodes-connectable": false,
            "edges-updatable": false,
            "zoom-on-double-click": false,
            "fit-view-on-init": "",
            onNodeClick: selectNode,
            onNodeDoubleClick: editNode,
            onNodeDragStop: remember
          }, {
            "node-scene-area": _withCtx((slot) => [
              _createElementVNode("div", {
                class: _normalizeClass(["area-box", ['area-' + slot.data.kind, { 'area-group': slot.data.kind !== 'sub', 'is-selected': isSelected(slot.data), 'is-missing': slot.data.missing }]])
              }, [
                (slot.data.kind === 'world' && slot.data.selection)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _createVNode(_unref(Handle), {
                        id: "in",
                        type: "target",
                        position: _unref(Position).Left,
                        connectable: false
                      }, null, 8, ["position"]),
                      _createVNode(_unref(Handle), {
                        id: "out",
                        type: "source",
                        position: _unref(Position).Right,
                        connectable: false
                      }, null, 8, ["position"])
                    ], 64))
                  : _createCommentVNode("", true),
                _createElementVNode("div", {
                  class: "area-heading",
                  title: slot.data.title
                }, [
                  _createElementVNode("span", _hoisted_5, _toDisplayString(slot.data.kind === 'world' ? '世界' : slot.data.kind === 'main' ? '一级' : '二级'), 1),
                  _createElementVNode("strong", null, _toDisplayString(slot.data.title), 1),
                  (slot.data.entityId !== undefined)
                    ? (_openBlock(), _createElementBlock("small", _hoisted_6, "#" + _toDisplayString(slot.data.entityId), 1))
                    : _createCommentVNode("", true)
                ], 8, _hoisted_4),
                (slot.data.kind !== 'sub')
                  ? (_openBlock(), _createElementBlock("span", _hoisted_7, _toDisplayString(slot.data.count) + " 个" + _toDisplayString(slot.data.kind === 'world' ? '一级区域' : '二级区域'), 1))
                  : _createCommentVNode("", true),
                (slot.data.kind !== 'sub' && !slot.data.count)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_8, "暂无下属区域"))
                  : _createCommentVNode("", true)
              ], 2)
            ]),
            default: _withCtx(() => [
              _createVNode(_unref(Background), {
                gap: 24,
                size: 1,
                "pattern-color": "#c3cce4"
              })
            ]),
            _: 1
          }, 8, ["nodes", "edges", "disable-keyboard-a11y"]))
        : (_openBlock(), _createElementBlock("div", _hoisted_9, "暂无世界，点击右上角“新增世界”开始。"))
    ], 34),
    _createElementVNode("div", _hoisted_10, [
      (selectedBox.value?.data.selection)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createElementVNode("span", null, _toDisplayString(selectedBox.value.data.title) + " · #" + _toDisplayString(selectedBox.value.data.entityId), 1),
            _createElementVNode("button", {
              type: "button",
              onClick: _cache[5] || (_cache[5] = ($event: any) => (emit('edit', selectedBox.value.data.selection)))
            }, "编辑所选属性")
          ], 64))
        : (_openBlock(), _createElementBlock("span", _hoisted_11, "选择世界或区域查看属性"))
    ])
  ]))
}
}

})